import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Play } from "lucide-react";

const W = 400, H = 300, PADDLE_W = 70, BALL_R = 6;
const COLS = 8, ROWS = 4, BRICK_H = 16, GAP = 4;

export function BrickBreaker({ onScore }) {
  const canvasRef = useRef(null);
  const [running, setRunning] = useState(false);
  const [over, setOver] = useState(false);
  const [score, setScore] = useState(0);
  const state = useRef(null);
  const reported = useRef(false);

  const init = () => {
    const bw = (W - GAP * (COLS + 1)) / COLS;
    const bricks = [];
    for (let r = 0; r < ROWS; r++)
      for (let c = 0; c < COLS; c++)
        bricks.push({ x: GAP + c * (bw + GAP), y: 40 + r * (BRICK_H + GAP), w: bw, alive: true, color: ["#00F0FF", "#39FF14", "#EFFF04", "#FF003C"][r] });
    state.current = { paddleX: W / 2 - PADDLE_W / 2, ballX: W / 2, ballY: H - 40, vx: 3, vy: -3, bricks, lives: 3, score: 0 };
  };

  const start = () => { init(); setScore(0); setOver(false); setRunning(true); reported.current = false; };

  useEffect(() => {
    if (!running) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let raf;
    const loop = () => {
      const s = state.current;
      s.ballX += s.vx; s.ballY += s.vy;
      if (s.ballX < BALL_R || s.ballX > W - BALL_R) s.vx *= -1;
      if (s.ballY < BALL_R) s.vy *= -1;
      // paddle
      if (s.ballY > H - 20 - BALL_R && s.ballX > s.paddleX && s.ballX < s.paddleX + PADDLE_W && s.vy > 0) {
        s.vy *= -1;
        s.vx += (s.ballX - (s.paddleX + PADDLE_W / 2)) * 0.05;
      }
      if (s.ballY > H) {
        s.lives -= 1;
        if (s.lives <= 0) { setRunning(false); setOver(true); setScore(s.score); }
        else { s.ballX = W / 2; s.ballY = H - 40; s.vx = 3; s.vy = -3; }
      }
      // bricks
      for (const b of s.bricks) {
        if (b.alive && s.ballX > b.x && s.ballX < b.x + b.w && s.ballY > b.y && s.ballY < b.y + BRICK_H) {
          b.alive = false; s.vy *= -1; s.score += 10; setScore(s.score);
        }
      }
      if (s.bricks.every((b) => !b.alive)) { setRunning(false); setOver(true); setScore(s.score); }
      // draw
      ctx.fillStyle = "#09090b"; ctx.fillRect(0, 0, W, H);
      for (const b of s.bricks) { if (b.alive) { ctx.fillStyle = b.color; ctx.fillRect(b.x, b.y, b.w, BRICK_H); } }
      ctx.fillStyle = "#00F0FF"; ctx.fillRect(s.paddleX, H - 20, PADDLE_W, 8);
      ctx.beginPath(); ctx.arc(s.ballX, s.ballY, BALL_R, 0, Math.PI * 2); ctx.fillStyle = "#fff"; ctx.fill();
      ctx.fillStyle = "#a1a1aa"; ctx.font = "12px monospace"; ctx.fillText(`Lives: ${s.lives}`, 8, 16);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    const move = (e) => {
      const rect = canvas.getBoundingClientRect();
      const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
      state.current.paddleX = Math.max(0, Math.min(W - PADDLE_W, (x / rect.width) * W - PADDLE_W / 2));
    };
    canvas.addEventListener("mousemove", move);
    canvas.addEventListener("touchmove", move);
    return () => { cancelAnimationFrame(raf); canvas.removeEventListener("mousemove", move); canvas.removeEventListener("touchmove", move); };
  }, [running]);

  useEffect(() => { if (over && !reported.current) { reported.current = true; onScore?.(score); } }, [over, score, onScore]);

  return (
    <div className="flex flex-col items-center" data-testid="brick-breaker">
      <p className="font-mono text-neon-cyan text-lg mb-3" data-testid="brick-score">SCORE: {score}</p>
      <div className="relative border-2 border-zinc-700" style={{ width: "min(92vw, 400px)" }}>
        <canvas ref={canvasRef} width={W} height={H} className="w-full block" style={{ touchAction: "none" }} />
        {!running && (
          <div className="absolute inset-0 bg-black/85 flex flex-col items-center justify-center gap-3">
            {over && <p className="font-display font-black text-2xl text-neon-pink uppercase" data-testid="brick-over">Game Over · {score}</p>}
            <Button onClick={start} data-testid="brick-start" className="bg-neon-cyan text-black rounded-none font-bold uppercase">
              <Play className="w-4 h-4 mr-1 fill-black" /> {over ? "Play Again" : "Start"}
            </Button>
          </div>
        )}
      </div>
      <p className="text-zinc-500 text-xs font-mono mt-3">Move your mouse to control the paddle. Break all the bricks!</p>
    </div>
  );
}
